import * as React from 'react'
import ReactDOM from 'react-dom'
import {Util, UUID} from 'yougile-util'
import {cn} from '@containers/html-utils'
import {ClientUtilities} from '../client/client-utilities'

interface ProgressItem {
  id: UUID
  file: File
  progress: number
}

interface Props {
  // id, file and progress in %
  files: ProgressItem[]
}

export class FileUploadProgress extends React.Component<Props> {
  private static rootElement: HTMLElement
  public static files: ProgressItem[]

  public static saveFile(
    file: File,
    opts?: {
      beforeCompletion?: (url: string) => Promise<void>
    }
  ): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const id = Util.UUID()
      FileUploadProgress.addFile(id, file)
      void ClientUtilities.saveFile(
        {file},
        async (url: string) => {
          if (url && opts?.beforeCompletion) {
            await opts.beforeCompletion(url)
          }
          FileUploadProgress.removeFile(id)
          if (!url) {
            reject(new Error('Failed to save file'))
            return
          }
          resolve(url)
        },
        (progress: number) => {
          FileUploadProgress.updateProgress(id, progress)
        }
      )
    })
  }

  public static update(): void {
    if (!FileUploadProgress.rootElement) {
      throw new Error('Not initialized yet')
    }

    ReactDOM.render(
      <FileUploadProgress files={FileUploadProgress.files} />,
      FileUploadProgress.rootElement
    )
  }

  public static init(): void {
    if (FileUploadProgress.rootElement) {
      return
    }
    const el = document.createElement('div')
    FileUploadProgress.rootElement = el
    FileUploadProgress.files = []
    document.body.appendChild(el)
    FileUploadProgress.update()
  }

  public static addFile(id: UUID, file: File): void {
    FileUploadProgress.files.push({id, file, progress: 0})
    FileUploadProgress.update()
  }
  public static changeFile(id: UUID, file: File): void {
    const f = FileUploadProgress.files.find(f => f.id === id)
    if (!f) {
      return
    }

    f.file = file
    FileUploadProgress.update()
  }

  public static updateProgress(id: UUID, progress: number): void {
    const item = FileUploadProgress.files.find((v: ProgressItem) => v.id === id)
    if (!item) {
      throw new Error('File was not found (file-upload-progress)')
    }
    item.progress = progress
    FileUploadProgress.update()
  }

  public static removeFile(id: UUID): void {
    const ix = FileUploadProgress.files.findIndex(
      (v: ProgressItem) => v.id === id
    )
    if (ix === -1) {
      throw new Error('File was not found (file-upload-progress)')
    }
    FileUploadProgress.files.splice(ix, 1)
    FileUploadProgress.update()
  }

  constructor(props: Props) {
    super(props)
  }

  public override render(): React.ReactNode {
    const files = this.props.files.map((v: ProgressItem) => (
      <div className="file-upload-progress-item" key={v.id}>
        <div className="file-upload-progress-name">
          {v.file.name && (
            <>
              <i className="fi-page" />
              {v.file.name}
            </>
          )}
          {!!v.file.size && (
            <span className="file-upload-progress-size">
              ({Util.humanReadableSize(v.file.size)})
            </span>
          )}
        </div>
        <div className="file-upload-progress-bar">
          <div
            className="file-upload-progress-value"
            style={{width: v.progress + '%'}}
          />
        </div>
      </div>
    ))

    const cls = cn('file-upload-progress z-[9999]', files.length && 'active')
    return <div className={cls}>{files}</div>
  }
}
